(() => {
  if (window.__athomeSiteHeaderLoaded) return;
  window.__athomeSiteHeaderLoaded = true;

  const closeDesktopMenus = (header, except = null) => {
    header.querySelectorAll('.ath-site-header__desktop-item.is-open').forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      delete item.dataset.athPersistent;
      item.querySelector('.ath-site-header__desktop-trigger')?.setAttribute('aria-expanded', 'false');
    });
  };

  const initHeader = (header) => {
    if (header.dataset.athInitialized) return;
    header.dataset.athInitialized = 'true';

    const toggle = header.querySelector('[data-ath-menu-toggle]');
    const mobileMenu = header.querySelector('[data-ath-mobile-menu]');
    const panels = [...header.querySelectorAll('[data-ath-mobile-panel]')];
    const desktopItems = [...header.querySelectorAll('.ath-site-header__desktop-item.has-children')];
    const searchInputs = [...header.querySelectorAll('.ath-site-header__search-input')];
    let returnFocus = null;
    let closeTimer = null;
    let panelTimer = null;
    let currentPanelName = 'root';
    let mobileSearchOriginY = null;
    let mobileSearchQuery = '';
    let mobileSearchAwaitingClear = false;
    let mobileSearchRestoreFrame = null;
    let mobileSearchMirrorTimer = null;
    let mobileSearchMirrorFrame = null;
    let mobileSearchWasOpen = document.body.classList.contains('snize-instant-widget-is-open');
    let mobileSearchCloseHandled = false;
    let searchFocusOrigin = null;
    let searchFocusRestoreFrame = null;
    let searchFocusRestoreTimers = [];
    let searchFocusRestoreToken = 0;
    let mobileMenuOriginY = null;
    let stableWindowScrollY = window.scrollY;
    let windowScrollSettleTimer = null;
    let pendingWindowScrollY = window.scrollY;
    let pendingWindowScrollDirection = 0;
    let reversedWindowScrollOriginY = null;

    const stabilizeWindowScroll = (originY) => {
      if (!Number.isFinite(originY)) return;
      const restore = () => {
        const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        window.scrollTo({ top: Math.min(originY, maxScrollY), left: 0, behavior: 'auto' });
      };
      restore();
      window.requestAnimationFrame(restore);
    };

    const trackStableWindowScroll = () => {
      const wasSettling = windowScrollSettleTimer !== null;
      const nextScrollY = window.scrollY;
      const nextDirection = Math.sign(nextScrollY - pendingWindowScrollY);
      if (!wasSettling) {
        pendingWindowScrollDirection = nextDirection;
        reversedWindowScrollOriginY = null;
      } else if (nextDirection && pendingWindowScrollDirection && nextDirection !== pendingWindowScrollDirection) {
        reversedWindowScrollOriginY = pendingWindowScrollY;
        pendingWindowScrollDirection = nextDirection;
      }
      pendingWindowScrollY = nextScrollY;
      window.clearTimeout(windowScrollSettleTimer);
      windowScrollSettleTimer = window.setTimeout(() => {
        windowScrollSettleTimer = null;
        if (document.body.classList.contains('ath-mobile-nav-open')) return;
        if (document.body.classList.contains('snize-instant-widget-is-open')) return;
        stableWindowScrollY = window.scrollY;
        pendingWindowScrollY = window.scrollY;
        pendingWindowScrollDirection = 0;
        reversedWindowScrollOriginY = null;
      }, 120);
    };

    const getInteractionScrollY = () => {
      if (windowScrollSettleTimer !== null && reversedWindowScrollOriginY !== null) {
        return reversedWindowScrollOriginY;
      }
      if (windowScrollSettleTimer !== null && window.scrollY < stableWindowScrollY - 1) {
        return stableWindowScrollY;
      }
      return window.scrollY;
    };

    window.addEventListener('scroll', trackStableWindowScroll, { passive: true });

    const rememberMobileSearchScroll = () => {
      if (mobileSearchOriginY !== null) return;
      mobileSearchOriginY = window.scrollY;
    };

    const restoreMobileSearchScroll = (originY = mobileSearchOriginY) => {
      if (originY === null) return;
      window.scrollTo({ top: originY, left: 0, behavior: 'auto' });
    };

    const queueMobileSearchRestore = () => {
      if (mobileSearchOriginY === null) return;
      if (document.body.classList.contains('snize-instant-widget-is-open')) return;
      window.cancelAnimationFrame(mobileSearchRestoreFrame);
      const originY = mobileSearchOriginY;
      mobileSearchOriginY = null;
      mobileSearchRestoreFrame = window.requestAnimationFrame(() => {
        mobileSearchRestoreFrame = null;
        const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const targetY = Math.min(originY, maxScrollY);
        if (Math.abs(window.scrollY - targetY) > 1) restoreMobileSearchScroll(targetY);
      });
    };

    const getMobileFocusables = () => {
      if (!mobileMenu?.classList.contains('is-open')) return [];
      const activePanel = mobileMenu.querySelector('.ath-site-header__mobile-panel.is-active');
      const candidates = [toggle, ...activePanel?.querySelectorAll('a[href], button:not([disabled])') || []];
      return candidates.filter((element) => element && !element.hidden && element.getClientRects().length > 0);
    };

    const setMenuTop = () => {
      header.style.setProperty('--ath-mobile-menu-top', `${Math.max(0, header.offsetHeight)}px`);
    };

    const showPanel = (name, { direction = 'forward', immediate = false, focus = true } = {}) => {
      const current = panels.find((panel) => panel.dataset.athMobilePanel === currentPanelName);
      const next = panels.find((panel) => panel.dataset.athMobilePanel === name);
      if (!next) return;
      window.clearTimeout(panelTimer);

      panels.forEach((panel) => {
        panel.classList.remove('is-staged-left', 'is-staged-right', 'is-exiting-left', 'is-exiting-right');
        panel.setAttribute('aria-hidden', String(panel !== next));
      });

      if (immediate || !current || current === next) {
        panels.forEach((panel) => panel.classList.toggle('is-active', panel === next));
        currentPanelName = name;
        if (focus) next.querySelector('button, a')?.focus({ preventScroll: true });
        return;
      }

      const forward = direction === 'forward';
      next.classList.remove('is-active');
      next.classList.add(forward ? 'is-staged-right' : 'is-staged-left');
      next.getBoundingClientRect();
      current.classList.remove('is-active');
      current.classList.add(forward ? 'is-exiting-left' : 'is-exiting-right');
      next.classList.remove('is-staged-left', 'is-staged-right');
      next.classList.add('is-active');
      currentPanelName = name;

      panelTimer = window.setTimeout(() => {
        current.classList.remove('is-exiting-left', 'is-exiting-right');
        if (focus) next.querySelector('button, a')?.focus({ preventScroll: true });
      }, 250);
    };

    const setActiveMegaItem = (item) => {
      const mega = item?.closest('.ath-site-header__mega-primary');
      if (!mega) return;
      mega.querySelectorAll('.ath-site-header__mega-item.is-active').forEach((active) => active.classList.remove('is-active'));
      item.classList.add('is-active');
    };

    const primeMega = (item) => {
      const firstWithChildren = item?.querySelector('.ath-site-header__mega-item.has-children');
      if (firstWithChildren) setActiveMegaItem(firstWithChildren);
    };

    const openDesktopMenu = (item) => {
      if (!item) return;
      window.clearTimeout(closeTimer);
      closeDesktopMenus(header, item);
      item.classList.add('is-open');
      item.querySelector('.ath-site-header__desktop-trigger')?.setAttribute('aria-expanded', 'true');
      primeMega(item);
    };

    const scheduleDesktopClose = (item) => {
      window.clearTimeout(closeTimer);
      if (item.dataset.athPersistent === 'true') return;
      closeTimer = window.setTimeout(() => {
        item.classList.remove('is-open');
        delete item.dataset.athPersistent;
        item.querySelector('.ath-site-header__desktop-trigger')?.setAttribute('aria-expanded', 'false');
      }, 140);
    };

    const closeMobileMenu = ({ restoreFocus = true } = {}) => {
      if (!mobileMenu || !toggle) return;
      const originY = mobileMenuOriginY ?? window.scrollY;
      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      mobileMenu.inert = true;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.querySelector('.visually-hidden').textContent = 'Open menu';
      document.documentElement.classList.remove('ath-mobile-nav-open');
      document.body.classList.remove('ath-mobile-nav-open');
      showPanel('root', { immediate: true, focus: false });
      if (restoreFocus) {
        const focusTarget = returnFocus?.isConnected ? returnFocus : toggle;
        focusTarget?.focus?.({ preventScroll: true });
        window.requestAnimationFrame(() => {
          if (mobileMenu.classList.contains('is-open') || !focusTarget?.isConnected) return;
          const active = document.activeElement;
          if (active === document.body || active === document.documentElement || active === focusTarget) {
            focusTarget.focus({ preventScroll: true });
          }
        });
      }
      stabilizeWindowScroll(originY);
      mobileMenuOriginY = null;
    };

    const openMobileMenu = ({ focusPanel = false } = {}) => {
      if (!mobileMenu || !toggle) return;
      if (mobileMenuOriginY === null) mobileMenuOriginY = getInteractionScrollY();
      returnFocus = toggle;
      closeDesktopMenus(header);
      document.documentElement.classList.add('ath-mobile-nav-open');
      document.body.classList.add('ath-mobile-nav-open');
      setMenuTop();
      mobileMenu.inert = false;
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.querySelector('.visually-hidden').textContent = 'Close menu';
      showPanel('root', { immediate: true, focus: false });
      stabilizeWindowScroll(mobileMenuOriginY);
      if (!focusPanel) return;
      window.requestAnimationFrame(() => {
        if (!mobileMenu.classList.contains('is-open')) return;
        mobileMenu.querySelector('.ath-site-header__mobile-panel.is-active a, .ath-site-header__mobile-panel.is-active button')?.focus({ preventScroll: true });
      });
    };

    const resetMobileMenuForPageTransition = () => {
      window.clearTimeout(panelTimer);
      if (mobileMenu) {
        mobileMenu.classList.remove('is-open');
        mobileMenu.setAttribute('aria-hidden', 'true');
        mobileMenu.inert = true;
      }
      panels.forEach((panel) => {
        const isRoot = panel.dataset.athMobilePanel === 'root';
        panel.classList.remove('is-staged-left', 'is-staged-right', 'is-exiting-left', 'is-exiting-right');
        panel.classList.toggle('is-active', isRoot);
        panel.setAttribute('aria-hidden', String(!isRoot));
      });
      currentPanelName = 'root';
      document.documentElement.classList.remove('ath-mobile-nav-open');
      document.body.classList.remove('ath-mobile-nav-open');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        const toggleLabel = toggle.querySelector('.visually-hidden');
        if (toggleLabel) toggleLabel.textContent = 'Open menu';
      }
      mobileMenuOriginY = null;
      returnFocus = null;
    };

    searchInputs.forEach((input) => {
      const form = input.closest('.ath-site-header__search');
      const clear = form?.querySelector('[data-ath-search-clear]');
      if (!clear) return;

      const syncClear = () => {
        clear.hidden = input.value.trim().length === 0;
      };

      const closeSuggestions = () => {
        const isMobileSearch = Boolean(input.closest('.ath-site-header__bottom-mobile'));
        if (isMobileSearch) {
          rememberMobileSearchScroll();
          mobileSearchCloseHandled = true;
          mobileSearchQuery = '';
          mobileSearchAwaitingClear = false;
          clearMobileOverlayQuery();
          input.value = '';
          input.blur();
          document.querySelector('#snize-instant-search-results.snize-ac-results-mobile #snize-mobile-search-input')?.blur();
          clear.hidden = true;
          if (typeof window.Searchanise?.AutocompleteCloseAll === 'function') {
            window.Searchanise.AutocompleteCloseAll();
          }
          if (document.body.classList.contains('snize-instant-widget-is-open')) {
            document.querySelector(
              '#snize-instant-search-results.snize-ac-results-mobile .snize-close-button-arrow'
            )?.click();
          } else {
            queueMobileSearchRestore();
            queueVisibleSearchFocusRestore();
          }
          return;
        }

        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('search', { bubbles: true }));
        input.blur();
        clear.hidden = true;
        queueVisibleSearchFocusRestore();
      };

      const closeNavBeforeMobileSearch = () => {
        if (input.closest('.ath-site-header__bottom-mobile') && mobileMenu?.classList.contains('is-open')) {
          closeMobileMenu({ restoreFocus: false });
        }
      };

      input.addEventListener('input', syncClear);
      input.addEventListener('search', syncClear);
      input.addEventListener('pointerdown', () => {
        searchFocusOrigin = input;
        closeNavBeforeMobileSearch();
        if (input.closest('.ath-site-header__bottom-mobile')) rememberMobileSearchScroll();
      }, true);
      input.addEventListener('focus', () => {
        searchFocusOrigin = input;
        closeNavBeforeMobileSearch();
        if (input.closest('.ath-site-header__bottom-mobile')) rememberMobileSearchScroll();
      });
      clear.addEventListener('click', closeSuggestions);
      syncClear();
    });

    const mobileSearchInput = null; // The API search owns mobile autocomplete on this theme.
    const mobileSearchClear = mobileSearchInput?.closest('.ath-site-header__search')?.querySelector('[data-ath-search-clear]');
    const inferMobileQueryFromResults = () => {
      const suggestion = document.querySelector('#snize-instant-search-results.snize-ac-results-mobile .snize-suggestion');
      const completion = suggestion?.querySelector('b')?.textContent || '';
      const suggestionLabel = suggestion?.getAttribute('aria-label') || '';
      if (!suggestionLabel || !completion || !suggestionLabel.endsWith(completion)) return;
      const inferredQuery = suggestionLabel.slice(0, -completion.length).trim();
      if (inferredQuery) mobileSearchQuery = inferredQuery;
    };
    const clearMobileOverlayQuery = () => {
      const overlayInput = document.querySelector('#snize-instant-search-results.snize-ac-results-mobile #snize-mobile-search-input');
      if (!overlayInput) return;
      overlayInput.classList.remove('ath-search-query-visible');
      overlayInput.placeholder = 'Search our store...';
      overlayInput.setAttribute('aria-label', 'Search our store');
    };
    const syncMobileOverlayQuery = () => {
      const overlayInput = document.querySelector('#snize-instant-search-results.snize-ac-results-mobile #snize-mobile-search-input');
      const suggestion = document.querySelector('#snize-instant-search-results.snize-ac-results-mobile .snize-suggestion');
      if (mobileSearchAwaitingClear) {
        mobileSearchQuery = '';
        clearMobileOverlayQuery();
        if (!suggestion) mobileSearchAwaitingClear = false;
        return;
      }
      inferMobileQueryFromResults();
      if (!overlayInput || !mobileSearchQuery) return;
      if (!overlayInput.value) overlayInput.value = mobileSearchQuery;
      overlayInput.classList.add('ath-search-query-visible');
      overlayInput.placeholder = mobileSearchQuery;
      overlayInput.setAttribute('aria-label', `Search for ${mobileSearchQuery}`);
    };
    document.addEventListener('beforeinput', (event) => {
      const isHeaderInput = event.target === mobileSearchInput;
      const isOverlayInput = event.target.matches?.('#snize-instant-search-results.snize-ac-results-mobile #snize-mobile-search-input');
      if (!isHeaderInput && !isOverlayInput) return;
      if (!event.inputType?.startsWith('insert') || !event.data) return;
      const currentValue = event.target.value || mobileSearchQuery;
      const selectionStart = Number.isInteger(event.target.selectionStart) ? event.target.selectionStart : currentValue.length;
      const selectionEnd = Number.isInteger(event.target.selectionEnd) ? event.target.selectionEnd : selectionStart;
      mobileSearchQuery = `${currentValue.slice(0, selectionStart)}${event.data}${currentValue.slice(selectionEnd)}`.trim();
      mobileSearchAwaitingClear = false;
      window.requestAnimationFrame(syncMobileOverlayQuery);
    }, true);
    document.addEventListener('input', (event) => {
      const isHeaderInput = event.target === mobileSearchInput;
      const isOverlayInput = event.target.matches?.('#snize-instant-search-results.snize-ac-results-mobile #snize-mobile-search-input');
      if (!isHeaderInput && !isOverlayInput) return;
      const query = event.target.value.trim();
      if (query) {
        mobileSearchQuery = query;
        mobileSearchAwaitingClear = false;
        window.requestAnimationFrame(syncMobileOverlayQuery);
      } else if (event.inputType?.startsWith('delete')) {
        mobileSearchQuery = '';
        clearMobileOverlayQuery();
      }
    }, true);
    const startMobileSearchMirror = () => {
      window.cancelAnimationFrame(mobileSearchMirrorFrame);
      window.clearTimeout(mobileSearchMirrorTimer);
      mobileSearchMirrorFrame = window.requestAnimationFrame(syncMobileOverlayQuery);
      mobileSearchMirrorTimer = window.setTimeout(() => {
        mobileSearchMirrorTimer = null;
        if (document.body.classList.contains('snize-instant-widget-is-open')) syncMobileOverlayQuery();
      }, 180);
    };
    mobileSearchInput?.addEventListener('pointerdown', startMobileSearchMirror, true);
    mobileSearchInput?.addEventListener('focus', startMobileSearchMirror, true);

    const resetMobileHeaderSearch = ({ preserveFocus = false } = {}) => {
      if (!mobileSearchInput) return;
      mobileSearchQuery = '';
      mobileSearchAwaitingClear = false;
      clearMobileOverlayQuery();
      mobileSearchInput.value = '';
      if (!preserveFocus) mobileSearchInput.blur();
      if (mobileSearchClear) mobileSearchClear.hidden = true;
    };

    const getVisibleSearchFocusTarget = () => {
      const candidates = [
        searchFocusOrigin,
        ...searchInputs,
      ];
      return candidates.find((candidate) => (
        candidate?.isConnected
        && candidate.getClientRects().length > 0
        && !candidate.closest('[aria-hidden="true"]')
      ));
    };

    const cancelVisibleSearchFocusRestore = () => {
      searchFocusRestoreToken += 1;
      window.cancelAnimationFrame(searchFocusRestoreFrame);
      searchFocusRestoreFrame = null;
      searchFocusRestoreTimers.forEach((timer) => window.clearTimeout(timer));
      searchFocusRestoreTimers = [];
    };

    const queueVisibleSearchFocusRestore = () => {
      const target = getVisibleSearchFocusTarget();
      if (!target) return;
      cancelVisibleSearchFocusRestore();
      const token = searchFocusRestoreToken;

      const restore = () => {
        if (token !== searchFocusRestoreToken || !target.isConnected) return;
        if (document.body.classList.contains('snize-instant-widget-is-open')) return;
        if (target.getClientRects().length === 0 || target.closest('[aria-hidden="true"]')) return;

        const active = document.activeElement;
        const activeBelongsToClosedSearch = (
          !active
          || active === document.body
          || active === document.documentElement
          || active === target
          || active.closest?.('[data-ath-search-clear]')
          || active.closest?.('#snize-instant-search-results')
        );
        if (!activeBelongsToClosedSearch) {
          cancelVisibleSearchFocusRestore();
          return;
        }
        target.focus({ preventScroll: true });
      };

      searchFocusRestoreFrame = window.requestAnimationFrame(() => {
        searchFocusRestoreFrame = null;
        restore();
      });
      [120, 320].forEach((delay, index, delays) => {
        const timer = window.setTimeout(() => {
          searchFocusRestoreTimers = searchFocusRestoreTimers.filter((pending) => pending !== timer);
          restore();
          if (index === delays.length - 1 && token === searchFocusRestoreToken) searchFocusOrigin = null;
        }, delay);
        searchFocusRestoreTimers.push(timer);
      });
    };

    document.addEventListener('pointerdown', (event) => {
      if (!event.target.closest?.('#snize-instant-search-results.snize-ac-results-mobile .snize-clear-button')) return;
      mobileSearchQuery = '';
      mobileSearchAwaitingClear = true;
      clearMobileOverlayQuery();
    }, true);

    const requestMobileSearchFocusReturn = (event) => {
      if (!event.target.closest?.('#snize-instant-search-results.snize-ac-results-mobile .snize-close-button, #snize-instant-search-results.snize-ac-results-mobile .snize-close-button-arrow')) return;
      if (mobileSearchCloseHandled) return;
      mobileSearchCloseHandled = true;
      rememberMobileSearchScroll();
      queueVisibleSearchFocusRestore();
    };
    document.addEventListener('pointerdown', requestMobileSearchFocusReturn, true);
    document.addEventListener('click', requestMobileSearchFocusReturn, true);

    const mobileSearchStateObserver = new MutationObserver(() => {
      const isOpen = document.body.classList.contains('snize-instant-widget-is-open');
      if (isOpen && !mobileSearchWasOpen) {
        mobileSearchCloseHandled = false;
        startMobileSearchMirror();
        window.requestAnimationFrame(syncMobileOverlayQuery);
        if (mobileSearchOriginY !== null) {
          const originY = mobileSearchOriginY;
          window.requestAnimationFrame(() => {
            if (!document.body.classList.contains('snize-instant-widget-is-open')) return;
            if (Math.abs(window.scrollY - originY) > 1) restoreMobileSearchScroll(originY);
          });
        }
      } else if (!isOpen && mobileSearchWasOpen) {
        window.cancelAnimationFrame(mobileSearchMirrorFrame);
        window.clearTimeout(mobileSearchMirrorTimer);
        mobileSearchMirrorTimer = null;
        resetMobileHeaderSearch({ preserveFocus: true });
        queueMobileSearchRestore();
        queueVisibleSearchFocusRestore();
      }
      mobileSearchWasOpen = isOpen;
    });
    mobileSearchStateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('pagehide', () => {
      mobileSearchStateObserver.disconnect();
      window.cancelAnimationFrame(mobileSearchMirrorFrame);
      window.clearTimeout(mobileSearchMirrorTimer);
      cancelVisibleSearchFocusRestore();
      resetMobileMenuForPageTransition();
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) resetMobileMenuForPageTransition();
    });

    toggle?.addEventListener('pointerdown', () => {
      if (!mobileMenu?.classList.contains('is-open')) mobileMenuOriginY = getInteractionScrollY();
    }, { passive: true });

    toggle?.addEventListener('click', (event) => {
      if (mobileMenu?.classList.contains('is-open')) closeMobileMenu();
      else openMobileMenu({ focusPanel: event.detail === 0 });
    });

    header.querySelectorAll('[data-ath-panel-target]').forEach((button) => {
      button.addEventListener('click', (event) => showPanel(button.dataset.athPanelTarget, {
        direction: 'forward',
        focus: event.detail === 0,
      }));
    });

    header.querySelectorAll('[data-ath-panel-back]').forEach((button) => {
      button.addEventListener('click', (event) => showPanel(button.dataset.athPanelBack, {
        direction: 'back',
        focus: event.detail === 0,
      }));
    });

    header.querySelectorAll('.ath-site-header__desktop-trigger').forEach((button) => {
      button.addEventListener('click', () => {
        const item = button.closest('.ath-site-header__desktop-item');
        if (item?.classList.contains('is-open') && item.dataset.athPersistent === 'true') {
          closeDesktopMenus(header);
        } else {
          openDesktopMenu(item);
          item.dataset.athPersistent = 'true';
        }
      });
    });

    desktopItems.forEach((item) => {
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') openDesktopMenu(item);
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch') scheduleDesktopClose(item);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) {
          delete item.dataset.athPersistent;
          scheduleDesktopClose(item);
        }
      });
      item.querySelectorAll('.ath-site-header__mega-item.has-children').forEach((megaItem) => {
        megaItem.addEventListener('pointerenter', () => setActiveMegaItem(megaItem));
        megaItem.addEventListener('focusin', () => setActiveMegaItem(megaItem));
      });
    });

    header.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!mobileMenu?.classList.contains('is-open')) return;

        const target = link.getAttribute('target');
        let destination = null;
        try {
          destination = new URL(link.href, window.location.href);
        } catch (_error) {
          // Invalid or non-navigation links can use the normal close behavior.
        }
        const isSameTabPageNavigation = (
          !event.defaultPrevented
          && !event.metaKey
          && !event.ctrlKey
          && !event.shiftKey
          && !event.altKey
          && !link.hasAttribute('download')
          && (!target || target === '_self')
          && destination
          && ['http:', 'https:'].includes(destination.protocol)
        );

        // Keep the menu covering the outgoing page. Closing it here restores the
        // old page's scroll position and creates a visible jump before navigation.
        if (isSameTabPageNavigation) return;
        closeMobileMenu({ restoreFocus: false });
      });
    });

    document.addEventListener('click', (event) => {
      if (!header.contains(event.target)) closeDesktopMenus(header);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (mobileMenu?.classList.contains('is-open')) closeMobileMenu();
        else closeDesktopMenus(header);
        return;
      }

      if (event.key !== 'Tab' || !mobileMenu?.classList.contains('is-open')) return;
      const focusables = getMobileFocusables();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!focusables.includes(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth >= 992 && mobileMenu?.classList.contains('is-open')) {
        closeMobileMenu({ restoreFocus: false });
      }
    }, { passive: true });

    const headerResizeObserver = new ResizeObserver(setMenuTop);
    headerResizeObserver.observe(header);

    if (mobileMenu) {
      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      mobileMenu.inert = true;
    }
    document.documentElement.classList.remove('ath-mobile-nav-open');
    document.body.classList.remove('ath-mobile-nav-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    setMenuTop();
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ath-site-header]').forEach(initHeader);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
